import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { alpha } from "@mui/material/styles";
import type { DashboardWidgetStatus } from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";

function getStatusCopy(status: Exclude<DashboardWidgetStatus, "ready">): string {
  switch (status) {
    case "loading":
      return "Loading…";
    case "empty":
      return "No data is currently available.";
    case "not_permitted":
      return "You do not have permission to view this data.";
    case "stale":
      return "Showing the latest available data.";
    case "unavailable":
      return "This data source is currently unavailable.";
    case "partial":
      return "Some data is currently unavailable.";
    case "error":
      return "Unable to load this data.";
  }
}

function getStatusExplanation(status: Exclude<DashboardWidgetStatus, "ready">): string {
  switch (status) {
    case "loading":
      return "The dashboard is retrieving the latest data for this source.";
    case "empty":
      return "The source responded successfully but has no records for the current view.";
    case "not_permitted":
      return "Your current role is not permitted to access this dashboard data source.";
    case "stale":
      return "The latest live reading is delayed. The displayed values are the most recent readings received.";
    case "unavailable":
      return "This source did not provide data in the latest dashboard snapshot.";
    case "partial":
      return "At least one value in the latest dashboard snapshot is unavailable. Other displayed values remain available.";
    case "error":
      return "The dashboard could not retrieve this source. Try refreshing the page.";
  }
}

function getStatusColor(
  status: Exclude<DashboardWidgetStatus, "ready">
): "info" | "warning" | "error" {
  if (status === "error" || status === "unavailable" || status === "not_permitted") return "error";
  if (status === "partial" || status === "stale") return "warning";
  return "info";
}

interface DashboardWidgetAccessStateProps {
  status: Exclude<DashboardWidgetStatus, "ready">;
  observedAt?: string;
  compact?: boolean;
  message?: string;
}

export function DashboardWidgetAccessState({
  status,
  observedAt,
  compact = false,
  message: messageOverride,
}: DashboardWidgetAccessStateProps) {
  const color = getStatusColor(status);
  const message =
    messageOverride ??
    `${getStatusCopy(status)}${
      observedAt != null && (status === "stale" || status === "partial")
        ? ` Last observed ${new Date(observedAt).toLocaleString()}.`
        : ""
    }`;

  return (
    <Tooltip title={getStatusExplanation(status)} arrow>
      <Box
        role={status === "error" || status === "unavailable" ? "alert" : "status"}
        sx={(theme) => {
          const palette =
            color === "error"
              ? theme.palette.error
              : color === "warning"
                ? theme.palette.warning
                : theme.palette.info;

          return {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            alignSelf: "flex-start",
            minHeight: compact ? 24 : 30,
            px: compact ? 0.75 : 1,
            py: compact ? 0.25 : 0.5,
            borderRadius: 0.75,
            color: palette.light,
            backgroundColor: alpha(palette.main, 0.1),
            border: `1px solid ${alpha(palette.main, 0.3)}`,
            cursor: "help",
          };
        }}
      >
        {status === "loading" ? (
          <CircularProgress size={compact ? 14 : 16} color={color} />
        ) : (
          <InfoOutlinedIcon sx={{ fontSize: compact ? "0.8rem" : "0.9rem" }} />
        )}
        <Typography variant={compact ? "caption" : "body2"} sx={{ color: "inherit" }}>
          {message}
        </Typography>
      </Box>
    </Tooltip>
  );
}
